// 進捗レポート送信ウィザード — 部署 → 週 → AI生成 → プレビュー → 宛先 → 送信確認。
// 送信自体は既存の digest-runs (ConfirmSendDialog) に引き継ぐ。
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '@/auth/AuthContext';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Buildings, CalendarBlank, Sparkle, PaperPlaneTilt, ArrowLeft, ArrowRight,
  CircleNotch, MagnifyingGlass, CheckCircle, FilePdf, EnvelopeSimple,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { n: 1, label: '対象を選択' },
  { n: 2, label: 'プレビュー' },
  { n: 3, label: '宛先を選択' },
];

// PDFではグラフとして描画されるチャートマーカーを、テキストバーに変換して表示
const prettyMarkdown = (md) => (md || '')
  .replace(/^%%bar\|(\d{1,3})\|\w+\|(.*?)%%$/gm, (_m, pct, label) => {
    const filled = Math.round(Number(pct) / 10);
    return `${label}: ${'▰'.repeat(filled)}${'▱'.repeat(10 - filled)} ${pct}%`;
  })
  .replace(/^%%healthbar\|(.+?)%%$/gm, (_m, g) => g.split('|')
    .map((s) => { const [label, count] = s.split(':'); return `${label} ${count}`; })
    .join(' ・ '));

export default function ProgressReportWizard({ open, onClose, onPrepared }) {
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(1);
  const [dept, setDept] = useState('all');
  const [weeks, setWeeks] = useState([]);          // selected week start dates
  const [lang, setLang] = useState('ja');
  const [includeReports, setIncludeReports] = useState(true);
  // 週間AIダイジェスト（技術部）を載せるか、どこに出すか。既定はON・本文内。
  const [includeWeeklyDigest, setIncludeWeeklyDigest] = useState(true);
  const [wdOutput, setWdOutput] = useState('inline');
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);        // compose result
  const [narrativeBlock, setNarrativeBlock] = useState(null);  // EN gate refusal
  const [subject, setSubject] = useState('');
  const [selected, setSelected] = useState([]);    // recipient emails
  const [search, setSearch] = useState('');
  const [preparing, setPreparing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep(1); setDraft(null); setSelected([]); setSearch('');
    axios.get(`${API}/api/digest-wizard/options`, { withCredentials: true })
      .then((r) => {
        setOpts(r.data);
        const cur = (r.data.weeks || []).find((w) => w.current);
        setWeeks(cur ? [cur.start] : []);
      })
      .catch((e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || '選択肢の取得に失敗しました'));
  }, [open]);

  const selectedWeeks = useMemo(() => {
    if (!opts || weeks.length === 0) return [];
    return opts.weeks
      .filter((w) => weeks.includes(w.start))
      .slice()
      .sort((a, b) => a.start.localeCompare(b.start))
      .map((w) => ({ start: w.start, end: w.end, label: w.label }));
  }, [opts, weeks]);

  const period = useMemo(() => {
    if (selectedWeeks.length === 0) return null;
    return { start: selectedWeeks[0].start, end: selectedWeeks[selectedWeeks.length - 1].end };
  }, [selectedWeeks]);

  const toggleWeek = (start) => setWeeks((prev) => (
    prev.includes(start) ? prev.filter((s) => s !== start) : [...prev, start]
  ));

  const generate = async () => {
    if (!period) { toast.error('週を1つ以上選択してください'); return; }
    setNarrativeBlock(null);
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/api/digest-wizard/compose`, {
        department_id: dept, start: period.start, end: period.end,
        weeks: selectedWeeks.map((w) => ({ start: w.start, end: w.end })),
        language: lang, include_reports: includeReports,
        include_weekly_digest: includeWeeklyDigest,
        weekly_digest_output: wdOutput,
      }, { withCredentials: true });
      setDraft(r.data);
      setSubject(r.data.subject || '');
      setStep(2);
    } catch (e) {
      // 全体の axios インターセプタ（AuthContext.js:57）が object の detail を
      // 文字列に潰し、構造体を `detail_obj` に退避する。ここで detail だけを見ると
      // `detail.code` が常に undefined になり、週リストと導線を持つ表示が出ない
      // （REPORT-EN-01 の表示もこの理由で出ていなかった）。
      const data = e?.response?.data;
      const detail = data?.detail_obj || data?.detail;
      // The English report GATES on the Weekly AI Digest translation and
      // refuses while it is missing (REPORT-EN-01). A toast disappears; the
      // reader needs the week list and the action, so it stays on the page.
      if (detail?.code === 'REPORT-EN-01' || detail?.code === 'REPORT-WD-01') {
        setNarrativeBlock(detail);
      } else {
        toast.error(formatApiErrorDetail(data?.detail) || 'レポート生成に失敗しました');
      }
    } finally { setGenerating(false); }
  };

  const prepare = async () => {
    if (selected.length === 0) { toast.error('宛先を1名以上選択してください'); return; }
    setPreparing(true);
    try {
      const r = await axios.post(`${API}/api/digest-runs`, {
        kind: 'eng_progress',
        period_start: draft.period_start,
        period_end: draft.period_end,
        subject,
        body: draft.body_html,
        markdown: draft.markdown,
        recipients: selected,
        // Full compose payload → backend renders the accessible PDF from
        // structured data (cover page, glossary, plain-language labels) instead
        // of parsing markdown. `metrics` is MANDATORY: it is the one shared
        // aggregation the e-mail body also renders. Omitting it made the
        // backend recompute without staff_total or the project alias table, so
        // the PDF read "x/0 staff" and split projects the e-mail had merged.
        payload_snapshot: {
          period_start: draft.period_start, period_end: draft.period_end,
          week_count: draft.week_count, department_name: draft.department_name,
          subject: draft.subject, language: draft.language || 'ja',
          metrics: draft.metrics,
          digest: draft.digest, weekly: draft.weekly || [],
          reports: draft.reports || [],
          person_digests: draft.person_digests || {},
          weekly_digests: draft.weekly_digests || [],
          weekly_digests_separate: draft.weekly_digests_separate || [],
          weekly_digest_output: draft.weekly_digest_output || 'none',
          ai_summary: draft.ai_summary,
        },
      }, { withCredentials: true });
      onPrepared?.(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || '送信準備に失敗しました');
    } finally { setPreparing(false); }
  };

  const candidates = useMemo(() => {
    const list = opts?.recipients || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) => `${r.name || ''} ${r.email} ${r.department || ''}`.toLowerCase().includes(q));
  }, [opts, search]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="progress-report-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <PaperPlaneTilt size={18} weight="duotone" className="text-teal-600" />
            進捗レポートを送信
          </DialogTitle>
          <DialogDescription className="text-[11.5px] text-zinc-500">
            部署と週を選んでAIレポートを生成し、プレビュー確認後に宛先を選んで送信します。
          </DialogDescription>
        </DialogHeader>

        {/* step indicator */}
        <div className="flex items-center gap-2 mb-1">
          {STEPS.map((s, i) => (
            <div key={s.n} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${
                step === s.n ? 'bg-teal-600 border-teal-600 text-white'
                  : step > s.n ? 'bg-teal-50 border-teal-200 text-teal-700'
                  : 'bg-white border-zinc-200 text-zinc-400'}`}>
                {step > s.n ? <CheckCircle size={12} weight="fill" /> : <span>{s.n}</span>}
                {s.label}
              </div>
              {i < STEPS.length - 1 && <div className="w-5 h-px bg-zinc-200" />}
            </div>
          ))}
        </div>

        {!opts && <p className="py-8 text-center text-[12px] text-zinc-400">読み込み中…</p>}

        {/* ── Step 1: 対象選択 ── */}
        {opts && step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="text-[12px] font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
                <Buildings size={14} className="text-teal-600" /> 対象部署のプロジェクト
              </label>
              <select value={dept} onChange={(e) => setDept(e.target.value)}
                data-testid="wizard-dept-select"
                className="w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500">
                <option value="all">全部署</option>
                {opts.departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-[12px] font-semibold text-zinc-700 flex items-center gap-1.5 mb-1.5">
                <CalendarBlank size={14} className="text-teal-600" /> 対象週（複数選択可）
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                {opts.weeks.map((w) => (
                  <button key={w.start} type="button" onClick={() => toggleWeek(w.start)}
                    data-testid={`wizard-week-${w.start}`}
                    className={`rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                      weeks.includes(w.start)
                        ? 'bg-teal-600 border-teal-600 text-white'
                        : 'bg-white border-zinc-200 text-zinc-600 hover:bg-teal-50'}`}>
                    {w.label}{w.current && <span className="block text-[9px] font-normal opacity-80">今週</span>}
                  </button>
                ))}
              </div>
              {period && (
                <p className="text-[11px] text-zinc-500 mt-1.5" data-testid="wizard-period">
                  レポート期間: <span className="font-semibold text-zinc-700">{period.start} 〜 {period.end}</span>
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <div>
                <span className="text-[12px] font-semibold text-zinc-700 block mb-1.5">言語</span>
                <div className="inline-flex rounded-lg border border-zinc-200 overflow-hidden">
                  {[{ v: 'ja', label: '日本語' }, { v: 'en', label: 'English' }].map((o) => (
                    <button key={o.v} type="button" onClick={() => setLang(o.v)}
                      data-testid={`wizard-lang-${o.v}`}
                      className={`px-3 py-1.5 text-[12px] font-semibold ${
                        lang === o.v ? 'bg-teal-600 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-zinc-700 mt-5 cursor-pointer">
                <input type="checkbox" checked={includeReports}
                  onChange={(e) => setIncludeReports(e.target.checked)}
                  data-testid="wizard-include-reports"
                  className="rounded border-zinc-300 text-teal-600 focus:ring-teal-500" />
                日報ハイライトを含める
              </label>
              <label className="flex items-center gap-2 text-[12px] text-zinc-700 mt-2.5 cursor-pointer">
                <input type="checkbox" checked={includeWeeklyDigest}
                  onChange={(e) => setIncludeWeeklyDigest(e.target.checked)}
                  data-testid="wizard-include-weekly-digest"
                  className="rounded border-zinc-300 text-teal-600 focus:ring-teal-500" />
                週間AIダイジェストを含める
              </label>
              {includeWeeklyDigest && (
                <div className="mt-2 ml-6">
                  <div className="text-[11px] font-semibold text-zinc-500 mb-1">出力先</div>
                  <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden">
                    {[{ v: 'inline', l: 'レポート内' },
                      { v: 'separate', l: '別PDF' },
                      { v: 'both', l: '両方' }].map((o) => (
                      <button key={o.v} type="button" onClick={() => setWdOutput(o.v)}
                        data-testid={`wizard-wd-output-${o.v}`}
                        className={`px-3 py-1 text-[12px] transition-colors ${
                          wdOutput === o.v ? 'bg-teal-600 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}>
                        {o.l}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-[10px] leading-snug text-zinc-500">
                    週ごとに1ブロックずつ、週の順に載せます。対象期間の週にダイジェストが
                    無い・中身が空・未完成の場合は作成せずに知らせます（黙って省きません）。
                  </p>
                </div>
              )}
            </div>

            {narrativeBlock && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900"
                data-testid="wizard-narrative-block">
                <p className="font-semibold">
                  {narrativeBlock.code === 'REPORT-WD-01'
                    ? '週間AIダイジェストを含めたレポートを生成できません'
                    : '英語レポートを生成できません'}
                </p>
                <p className="mt-1 leading-relaxed">{narrativeBlock.message}</p>
                <ul className="mt-1.5 list-disc pl-4">
                  {(narrativeBlock.weekly_digest_status?.missing
                    || narrativeBlock.narrative_status?.missing || []).map((w) => (
                    <li key={w.start} data-testid={`wizard-missing-week-${w.start}`}>
                      {w.start} 〜 {w.end}（{{
                        no_digest: 'ダイジェスト未生成',
                        empty_content: '中身が空',
                        incomplete: '一部セクション未生成',
                        not_translated: '英訳未完了',
                      }[w.reason] || w.reason || w.english_status}）
                    </li>
                  ))}
                </ul>
                <a href="/eng/reports?tab=digests" data-testid="wizard-translate-link"
                  className="mt-2 inline-block font-semibold underline">
                  {narrativeBlock.code === 'REPORT-WD-01'
                    ? '週間AIダイジェストを開く →'
                    : '週次AIダイジェストで英訳する →'}
                </a>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button onClick={generate} disabled={generating || !period}
                data-testid="wizard-generate-btn"
                className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5">
                {generating ? <CircleNotch size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
                {generating ? 'AIがレポートを生成中…' : 'AIレポートを生成'}
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 2: プレビュー ── */}
        {opts && step === 2 && draft && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span className="rounded-full bg-teal-50 border border-teal-200 text-teal-700 px-2 py-0.5 font-semibold">{draft.department_name}</span>
              <span className="rounded-full bg-zinc-50 border border-zinc-200 text-zinc-600 px-2 py-0.5">{draft.period_start} 〜 {draft.period_end}</span>
              <span className="rounded-full bg-zinc-50 border border-zinc-200 text-zinc-600 px-2 py-0.5">プロジェクト {draft.project_count} 件</span>
              {draft.report_count > 0 && <span className="rounded-full bg-zinc-50 border border-zinc-200 text-zinc-600 px-2 py-0.5">日報 {draft.report_count} 件</span>}
              {draft.ai_summary_used
                ? <span className="rounded-full bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 font-semibold">AIサマリー付き</span>
                : <span className="rounded-full bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5">AIサマリーなし（データのみ）</span>}
            </div>
            <div>
              <label className="text-[12px] font-semibold text-zinc-700 mb-1 block">件名</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)}
                data-testid="wizard-subject-input"
                className="w-full rounded-lg border border-zinc-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-zinc-700 mb-1 flex items-center gap-1.5">
                <EnvelopeSimple size={14} className="text-teal-600" /> メール本文プレビュー
              </label>
              <div className="rounded-lg border border-zinc-200 bg-white p-3 max-h-52 overflow-y-auto"
                data-testid="wizard-email-preview"
                dangerouslySetInnerHTML={{ __html: draft.body_html }} />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-zinc-700 mb-1 flex items-center gap-1.5">
                <FilePdf size={14} className="text-rose-500" /> 添付PDFの内容（送信前に最終確認画面でPDF表示できます）
              </label>
              <pre className="rounded-lg border border-zinc-200 bg-zinc-50 p-3 max-h-48 overflow-y-auto text-[11px] leading-relaxed whitespace-pre-wrap text-zinc-700"
                data-testid="wizard-markdown-preview">{prettyMarkdown(draft.markdown)}</pre>
            </div>
            <div className="flex justify-between pt-1">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-1" className="gap-1.5">
                <ArrowLeft size={14} /> 戻る
              </Button>
              <Button onClick={() => setStep(3)} data-testid="wizard-to-recipients"
                className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5">
                宛先の選択へ <ArrowRight size={14} />
              </Button>
            </div>
          </div>
        )}

        {/* ── Step 3: 宛先選択 ── */}
        {opts && step === 3 && draft && (
          <div className="space-y-3">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="名前・メール・部署で検索（例: 社長 / CEO）"
                data-testid="wizard-recipient-search"
                className="w-full rounded-lg border border-zinc-200 pl-8 pr-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div className="rounded-lg border border-zinc-200 max-h-56 overflow-y-auto divide-y divide-zinc-100"
              data-testid="wizard-recipient-list">
              {candidates.length === 0 && (
                <p className="px-3 py-6 text-center text-[12px] text-zinc-400">該当する宛先がありません</p>
              )}
              {candidates.map((r) => {
                const on = selected.includes(r.email);
                return (
                  <button key={r.email} type="button"
                    onClick={() => setSelected((prev) => (on ? prev.filter((e) => e !== r.email) : [...prev, r.email]))}
                    data-testid={`wizard-recipient-${r.id}`}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${on ? 'bg-teal-50' : 'hover:bg-zinc-50'}`}>
                    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-teal-600 border-teal-600' : 'border-zinc-300 bg-white'}`}>
                      {on && <CheckCircle size={12} weight="fill" className="text-white" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[12.5px] font-semibold text-zinc-800 truncate">{r.name || r.email}</span>
                      <span className="block text-[10.5px] text-zinc-400 truncate">{r.email}{r.department ? ` · ${r.department}` : ''}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-500" data-testid="wizard-selected-count">
              選択中: <span className="font-semibold text-teal-700">{selected.length} 名</span>
              {selected.length > 0 && <span className="text-zinc-400"> — {selected.slice(0, 3).join(', ')}{selected.length > 3 ? ` 他${selected.length - 3}件` : ''}</span>}
            </p>
            <div className="flex justify-between pt-1">
              <Button variant="outline" onClick={() => setStep(2)} data-testid="wizard-back-2" className="gap-1.5">
                <ArrowLeft size={14} /> 戻る
              </Button>
              <Button onClick={prepare} disabled={preparing || selected.length === 0}
                data-testid="wizard-prepare-btn"
                className="bg-teal-700 hover:bg-teal-800 text-white gap-1.5">
                {preparing ? <CircleNotch size={14} className="animate-spin" /> : <PaperPlaneTilt size={14} weight="fill" />}
                送信内容の最終確認へ
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
